import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Header from "./components/navigation/Header";
import LoginPage from "./auth/pages/LoginPage/LoginPage";
import ForgotPasswordPage from "./auth/pages/ForgotPassword/ForgotPasswordPage";
import ResetPasswordPage from "./auth/pages/ResetPassword/ResetPasswordPage";
import PasswordResetSuccessPage from "./auth/pages/PasswordResetSuccess/PasswordResetSuccessPage";
import { pages, ROLES } from "./utils/page-permissions";
import EmployeeProfile from "./pages/EmployeeProfile";
import DepositoryFiles from './pages/LD/DepositoryFiles';
import ToDoTickets from "./pages/HR/CRM/ToDoTickets";
import TicketDetail from "./pages/HR/CRM/TicketDetail";
import MITCheckinDashboard from './pages/MITCheckinDashboard';
import NotFound from "./pages/NotFound/NotFound";
import Unauthorized from "./pages/Unauthorized/Unauthorized";
import useSessionTimeout from "./hooks/useSessionTimeout";
import "./utils/chartSetup"; // Global Chart.js registration

// Check if user is authenticated (has valid token and role)
function isAuthenticated() {
  const token = localStorage.getItem("authToken");
  const role = localStorage.getItem("role") || localStorage.getItem("userRole");
  return !!(token && role);
}

function ProtectedRoute({ element, allowed, userRole, isLoading }) {
  // Show nothing while checking auth status to prevent flash of content
  if (isLoading) return null;

  // Not authenticated - redirect to login
  if (!isAuthenticated()) return <Navigate to="/" replace />;

  // No role - redirect to login
  if (!userRole) return <Navigate to="/" replace />;

  // Convert userRole to lowercase for comparison since allowed roles are lowercase
  const normalizedRole = userRole.toLowerCase();

  // User doesn't have permission - show 403 unauthorized page
  return allowed.includes(normalizedRole) ? element : <Navigate to="/unauthorized" replace />;
}


// Session timeout configuration (in minutes)
const SESSION_TIMEOUT_MINUTES = 30;
const SESSION_WARNING_MINUTES = 5;

function App() {
  const location = useLocation();
  const [userRole, setUserRole] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Session timeout - auto-logout after 30 minutes of inactivity
  // Shows warning 5 minutes before logout
  useSessionTimeout(SESSION_TIMEOUT_MINUTES, SESSION_WARNING_MINUTES);

  // Update userRole when location changes (after login redirect)
  useEffect(() => {
    const role = localStorage.getItem("role") || localStorage.getItem('userRole') || '';
    console.log("App.js - userRole from localStorage:", role);
    setUserRole(role);
    setIsLoading(false);
  }, [location.pathname]);

  useEffect(() => {
    // ResizeObserver error handler
    const resizeObserverErrorHandler = (e) => {
      const errorMessages = [
        'ResizeObserver loop completed with undelivered notifications.',
        'ResizeObserver loop limit exceeded'
      ];

      if (errorMessages.some(msg => e.message?.includes(msg))) {
        e.stopImmediatePropagation();
        e.preventDefault();

        // Hide webpack dev server error overlay
        requestAnimationFrame(() => {
          const overlay = document.getElementById('webpack-dev-server-client-overlay');
          const overlayDiv = document.getElementById('webpack-dev-server-client-overlay-div');
          if (overlay) overlay.style.display = 'none';
          if (overlayDiv) overlayDiv.style.display = 'none';
        });
      }
    };

    // Attach error handler
    window.addEventListener('error', resizeObserverErrorHandler);

    // Clean console for staging/production
    const originalError = console.error;
    console.error = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('ResizeObserver loop')) {
        return;
      }
      originalError.apply(console, args);
    };

    // Cleanup
    return () => {
      window.removeEventListener('error', resizeObserverErrorHandler);
      console.error = originalError;
    };
  }, []);

  return (
    <div className="App">
      <Header />
      <main className="app-content">
        <Routes>
          {/* Authentication Routes */}
          <Route path="/" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/password-reset-success" element={<PasswordResetSuccessPage />} />
          <Route path="/unauthorized" element={<Unauthorized />} />


          {/* ROUTES AS PER PERMISSIONS */}
          {pages.map((page) =>
            page.children ? (
              <React.Fragment key={page.name}>
                {page.children.map((child) => (
                  <Route
                    key={child.path}
                    path={child.path}
                    element={
                      <ProtectedRoute element={child.element} allowed={child.allowed} userRole={userRole} isLoading={isLoading} />
                    }
                  />
                ))}
              </React.Fragment>
            ) : page.type !== 'external' ? (
              <Route
                key={page.path}
                path={page.path}
                element={
                  <ProtectedRoute element={page.element} allowed={page.allowed} userRole={userRole} isLoading={isLoading} />
                }
              />
            ) : null
          )}

          <Route
              key="/hr/crm/tickets/:ticketNumber"
              path="/hr/crm/tickets/:ticketNumber"
              element={
                <ProtectedRoute element={<TicketDetail />} allowed={Object.values(ROLES)} userRole={userRole} isLoading={isLoading} />
              }
            />
          <Route
            key="/hr/crm/todo-tickets/:ticketNumber"
            path="/hr/crm/todo-tickets/:ticketNumber"
            element={
              <ProtectedRoute element={<ToDoTickets />} allowed={[ROLES.ADMIN, ROLES.HR]} userRole={userRole} isLoading={isLoading} />
            }
          />
          <Route
            key="/employee/:id"
            path="/employee/:id"
            element={
              <ProtectedRoute element={<EmployeeProfile />} allowed={Object.values(ROLES)} userRole={userRole} isLoading={isLoading} />
            }
          />
          <Route
            key="/mit-checkins"
            path="/mit-checkins"
            element={
              <ProtectedRoute element={<MITCheckinDashboard />} allowed={[ROLES.ADMIN, ROLES.LD, ROLES.LD_DIRECTOR]} userRole={userRole} isLoading={isLoading} />
            }
          />
          <Route
            key="/flm-upload"
            path="/flm-upload"
            element={
              <ProtectedRoute element={<DepositoryFiles />} allowed={[ROLES.ADMIN, ROLES.LD, ROLES.LD_DIRECTOR]} userRole={userRole} isLoading={isLoading} />
            }
          />

          {/* 404 Catch-all Route - must be last */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </div>
  );
}

export default App;
